import { z } from 'zod';
import { RoleSchema } from './role.schema.js';
/**
 * JWT claims — v2 format (post phase 1.5).
 *
 * Replaces the legacy `{ sub, email, role, clientEmail }` shape.
 *
 *   sub                  : User.id (MongoDB ObjectId string)
 *   email                : User.email (login identifier)
 *   isSuperAdmin         : Swoft staff flag; super admins have no active
 *                          membership but can act cross-tenant
 *   activeMembership     : the Organisation the user is currently acting in
 *                          (null for super admins not scoped to an Org)
 *
 * On switch-organisation the backend regenerates a new JWT with the updated
 * activeMembership. The frontend clears the React Query cache on switch to
 * avoid leaking data across tenants (see REFACTORING_FRONTEND_PLAN.md §24).
 *
 * Migration strategy: legacy v1 tokens are rejected (force-logout on first
 * API call), users re-login into the v2 format. See ADR-021.
 */
export const ActiveMembershipClaimSchema = z.object({
    membershipId: z.string(),
    organisationId: z.string(),
    organisationName: z.string(),
    role: RoleSchema,
    // ADR-027 — Customer-scoped viewer. Empty for unrestricted (all roles
    // before ADR-027). Only honored at scope-check layer when role === 'viewer'.
    customerIds: z.array(z.string()).default([]),
});
/**
 * Shape signed into the token (input to jsonwebtoken.sign).
 * Does not include iat/exp/type — those are added by the signing layer.
 */
export const JwtInputClaimsV2Schema = z.object({
    sub: z.string(),
    email: z.string().email(),
    isSuperAdmin: z.boolean(),
    activeMembership: ActiveMembershipClaimSchema.nullable(),
});
/**
 * Shape returned after verification — carries iat/exp/type added by the
 * JWT library and the signing layer respectively.
 */
export const JwtClaimsV2Schema = JwtInputClaimsV2Schema.extend({
    type: z.enum(['access', 'refresh']),
    iat: z.number().optional(),
    exp: z.number().optional(),
});
//# sourceMappingURL=jwt-claims.schema.js.map